/**
 * PictureFormTest — validates the Create B&A Group form.
 *
 * The checks are unchanged. Two things about them are not:
 *
 *  1. THEY ARE SYNCHRONOUS. Every one was `async` while doing nothing
 *     asynchronous, so the only consumer had to `await` five times to read
 *     five property comparisons — and, more to the point, the results could
 *     not be used during render. `missingFields()` has to be readable while
 *     drawing the form, because that is where the list of what is still
 *     missing is shown. Callers that still `await` a plain value are fine.
 *
 *  2. `missingFields()` IS THE SOURCE, and `uploadValid()` derives from it.
 *     The form used to disable nothing and say nothing: a click on Upload
 *     called `uploadValid()`, got false, and returned silently. Writing the
 *     missing-field list in the component would have meant a second copy of
 *     these rules, free to drift from the one that actually gates the save.
 *     One list, used for both.
 */

/** Label for each failed check, in the order the form reads top to bottom. */
const FIELD_LABELS = {
  beforeId: "a Before photo",
  afterId: "an After photo",
  dateEntered: "the date completed",
  refinishers: "at least one refinisher",
  otherStain: "the custom stain name",
};

class PictureFormTest {
  constructor(formState) {
    this.beforeId = formState.beforeId;
    this.afterId = formState.afterId;
    this.dateEntered = formState.dateEntered;
    this.dateEnteredText = formState.dateEnteredText;
    this.refinishers = formState.refinishers;
    this.stainColor = formState.stainColor;
    this.stainColorChoice = formState.stainColorChoice;
  }

  testBeforeId() {
    return this.beforeId !== "";
  }

  testAfterId() {
    return this.afterId !== "";
  }

  testDateEntered() {
    return this.dateEntered !== "";
  }

  testDateEnteredText() {
    return !(this.dateEnteredText === "" || this.dateEnteredText.length < 8);
  }

  testRefinishers() {
    return (this.refinishers || []).length !== 0;
  }

  /**
   * Only meaningful while "Other" is the selected stain — then the free-text
   * field must hold something that is not the word "Other" itself.
   */
  testOther() {
    if (this.stainColorChoice === "Other") {
      return !(this.stainColor === "Other" || this.stainColor === "");
    }

    return true;
  }

  /** Human labels for every check that does not pass, form order. */
  missingFields() {
    const missing = [];

    if (!this.testBeforeId()) missing.push(FIELD_LABELS.beforeId);
    if (!this.testAfterId()) missing.push(FIELD_LABELS.afterId);
    if (!this.testDateEntered()) missing.push(FIELD_LABELS.dateEntered);
    if (!this.testRefinishers()) missing.push(FIELD_LABELS.refinishers);
    if (!this.testOther()) missing.push(FIELD_LABELS.otherStain);

    return missing;
  }

  /**
   * The save gate. Same five checks as before, now expressed as "nothing is
   * missing" so the gate and the on-screen list can never disagree.
   *
   * `testDateEnteredText` is deliberately NOT here — it never was. The form
   * has no `dateEnteredText` field any more; the date comes from
   * `EventDateTimeInput` as an epoch string.
   */
  uploadValid() {
    return this.missingFields().length === 0;
  }
}

export default PictureFormTest;
